import { Icon } from '@/icon/Icon';
import { truncate } from '@/util/string';
import { Chip } from '@mui/material';
import type { Column, Table } from '@tanstack/react-table';
import { useDataProvider } from '../DataProviderContext';
import { useTranslateValue } from '../hooks/useTranslateValue';
import { useExternalOptions } from './ExternalFilterOptionsContext';

function getColumnName(columnId: string, table: Table<any> | undefined) {
  const column = table?.getColumn(columnId);
  return column?.columnDef.meta?.name ?? columnId;
}

function useDisplayFilterValue(
  value: unknown,
  column: Column<any> | undefined
): string | unknown {
  const translated = useTranslateValue(value, column);
  const externalOptions = useExternalOptions(column);
  if (externalOptions) {
    return externalOptions.find((o) => o.id === value)?.displayName;
  }

  if (column?.columnDef?.meta?.filterLabel) {
    return column.columnDef.meta.filterLabel(translated);
  }
  if (Array.isArray(translated)) {
    return translated.join(' ... ');
  }
  return translated;
}

export const ActiveFilters = () => {
  const { table } = useDataProvider();
  const columnFilters = table?.getState()?.columnFilters ?? [];

  return (
    <>
      {columnFilters.map(({ id, value }) => (
        <ActiveFilterChip columnId={id} value={value} key={id} />
      ))}
    </>
  );
};

interface ActiveFilterChipProps {
  columnId: string;
  value: unknown;
}

const ActiveFilterChip = ({ columnId, value }: ActiveFilterChipProps) => {
  const { table } = useDataProvider();
  const column = table?.getColumn(columnId);
  const columnName = getColumnName(columnId, table);
  const displayValue = useDisplayFilterValue(value, column);

  const handleClear = () => {
    column?.setFilterValue(undefined);
  };

  return (
    <Chip
      label={[truncate(columnName, 20), truncate(displayValue, 20)].join(' ')}
      variant="outlined"
      color="primary"
      onDelete={handleClear}
      deleteIcon={<Icon name="cancel" size="small" />}
    />
  );
};
